import React, { useCallback, useEffect } from "react";

import { useNavigate } from "react-router";

import { Button, Flex, Form, Input, Typography } from "antd";
import type { FormInstance } from "antd";

import { useTranslation } from "react-i18next";

import { useSelector, useDispatch } from "react-redux";

import { MailOutlined, PhoneOutlined, UserOutlined } from "@ant-design/icons";
import AppSlice from "../../../../../App/reducer";

import { makeSelectRegistrationFormErrors } from "../../../../selectors";

import { RegisterFormProps, RegisterFormInputModel } from "./types";
import { phoneNumberRegExp } from "../../../../../../utils/regExps";

function RegisterForm({ loadingData, onRegister, onOpenLoginView }: RegisterFormProps) {
  const { t } = useTranslation("translations");
  const [form] = Form.useForm<RegisterFormInputModel>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formErrors = useSelector(makeSelectRegistrationFormErrors());

  useEffect(() => {
    if (formErrors) {
      const errors: Parameters<FormInstance["setFields"]>[0] = [];

      if (formErrors.find((x) => x.field === "login" && x.errorCode === "3")) {
        errors.push({
          name: "login",
          errors: [t("identification_number_already_registered")],
        });
      }

      if (formErrors.find((x) => x.field === "email" && x.errorCode === "3")) {
        errors.push({
          name: "password",
          errors: [t("email_already_registered")],
        });
      }
      form.setFields(errors);
    }
  }, [formErrors, form, t]);

  const onFinish = ({ login, companyName, phoneNumber, email }: RegisterFormInputModel) => {
    onRegister({ login, companyName, phoneNumber, email });
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.target.value.length >= 10 && e.target.value.length <= 12) {
        (async () => {
          try {
            const response = await fetch(`https://egrul.itsoft.ru/short_data/?${e.target.value}}`);
            const result: any = await response.json();
            form.setFields([
              {
                name: "companyName",
                value: result.full_name,
                errors: undefined,
              },
            ]);
          } catch (_err) {
            console.warn("No registered companies found");
          }
        })();
      }
    },
    [form],
  );

  const handleOpenPrivacyPolicy = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      dispatch(AppSlice.actions.closeAuthModal());
      navigate("/privacy/some1");
    },
    [dispatch, navigate],
  );

  return (
    <Form<RegisterFormInputModel> onFinish={onFinish} form={form} layout="vertical" requiredMark={false} preserve={false}>
      <Form.Item
        name="login"
        label={t("identification_number")}
        normalize={(value: string) => value.replace(/\D/g, "").slice(0, 12)}
        rules={[
          () => ({
            validator(_, value: string) {
              if (value && value.length >= 10 && value.length <= 12) {
                return Promise.resolve();
              }
              if (!value) {
                return Promise.reject(t("enter_identification_number"));
              }
              return Promise.reject(t("identification_number_length"));
            },
          }),
        ]}
      >
        <Input
          prefix={<UserOutlined aria-hidden />}
          inputMode="numeric"
          maxLength={12}
          autoComplete="username"
          aria-label={t("identification_number")}
          onBlur={handleBlur}
        />
      </Form.Item>

      <Form.Item name="companyName" label={t("company_name")} rules={[{ required: true, message: t("enter_company_name")! }]}>
        <Input autoComplete="organization" aria-label={t("company_name")} />
      </Form.Item>

      <Form.Item
        name="phoneNumber"
        label={t("phone_number")}
        rules={[
          () => ({
            validator(_, value: string) {
              if (phoneNumberRegExp.test(value)) {
                return Promise.resolve();
              }
              if (!value) {
                return Promise.reject(t("enter_phone_number"));
              }
              return Promise.reject(t("enter_phone_number_in_format"));
            },
          }),
        ]}
      >
        <Input prefix={<PhoneOutlined aria-hidden />} type="tel" inputMode="tel" autoComplete="tel" aria-label={t("phone_number")} />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: t("enter_email")! },
          { type: "email", message: t("wrong_email")! },
        ]}
      >
        <Input prefix={<MailOutlined aria-hidden />} type="email" autoComplete="email" aria-label="Email" />
      </Form.Item>

      <Typography.Paragraph type="secondary">
        {t("register_agreements_text")}{" "}
        <Typography.Link href="/privacy/some1" onClick={handleOpenPrivacyPolicy}>
          {t("policy_personal_date")}
        </Typography.Link>
      </Typography.Paragraph>

      <Form.Item>
        <Button type="primary" htmlType="submit" block loading={loadingData}>
          {t("register")}
        </Button>
      </Form.Item>

      <Flex align="center" justify="center" gap="small" wrap>
        <Typography.Text>{t("is_already_registered")}</Typography.Text>
        <Button onClick={onOpenLoginView} type="link" htmlType="button">
          {t("enter")}
        </Button>
      </Flex>
    </Form>
  );
}

export default React.memo(RegisterForm);
